/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import {
	type CSSProperties,
	memo,
	type ReactNode,
	useCallback,
	useEffect,
	useRef,
	useState,
} from 'react';

import { cssMap, jsx } from '@compiled/react';

import type ContainerAtRule from '@atlaskit/css/at-rules/container';
import useControlled from '@atlaskit/ds-lib/use-controlled';
import {
	type contentInsetBlockStart,
	type mainMinimumWidthVar,
	type UNSAFE_LayoutAreaState,
	UNSAFE_useLayoutAreaSizing,
} from '@atlaskit/navigation-system/layout/layout-sizing';
import { fg } from '@atlaskit/platform-feature-flags/fg';
import { token } from '@atlaskit/tokens';

import { usePanelActionsInternal } from '../../panel-manager/panel-provider';
import { usePanelStateInternal } from '../../panel-manager/use-panel-state-internal';
import { PanelRouter } from '../../panels-renderer/panel-router';
import { PanelsRenderer } from '../../panels-renderer/panels-renderer';
import { PanelSlot } from './panel-slot';

// Live sizing belongs to the layout and slot, not the routed entry point. Panel
// context updates still reach the router when its content or params change.
const StablePanelRouter = memo(PanelRouter);

const localPanelTrackWidthVar = '--p_lclPnlTrackW';
const localPanelResizingVar = '--p_lclPnlRsz';
const localPanelMinWidthVar = '--p_lclPnlMinW';

const styles = cssMap({
	container: {
		containerType: 'inline-size',
		containerName: 'local-panel-width',
		height: '100%',
		width: '100%',
	},
	fitContainer: {
		// Leave one pixel when the configured minima fit exactly; zero means overlay.
		// The query does not need a hardcoded Main/panel breakpoint.
		'--p_lclPnlHostW': '100cqw',
		containerType: 'inline-size',
		containerName: 'local-panel-layout',
		height: '100%',
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Shared layout CSS variable name.
		width: `max(0px, calc(var(--p_lclPnlHostW) - var(${'--n_mainMinW' satisfies typeof mainMinimumWidthVar}, 20rem) - var(--p_lclPnlMinW) + 1px))`,
	},
	layout: {
		display: 'grid',
		'--p_lclPnlArea': 'main',
		// Placement and cold presentation share the same local capacity query.
		'--p_lclPnlBg': token('elevation.surface.overlay'),
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Same typed sticky inset as the managed overlay.
		'--p_lclPnlInset':
			'calc(var(--n_bnrM, 0px) + var(--n_tNvM, 0px))' satisfies typeof contentInsetBlockStart,
		'--p_lclPnlBorder': 'none',
		'--p_lclPnlShadow': token('elevation.shadow.overlay'),
		gridTemplateAreas: `'main'`,
		gridTemplateColumns: 'minmax(0, 1fr)',
		gridTemplateRows: '1fr',
		height: '100%',
		width: 'var(--p_lclPnlHostW)',
		// This named container and its query are local to this component. Initial
		// layout responds to Main's actual space without waiting for client effects.
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-container-queries
		['@container local-panel-layout (min-width: 1px)' satisfies ContainerAtRule]: {
			'--p_lclPnlArea': 'panel',
			'--p_lclPnlCssW': '100%',
			'--p_lclPnlBg': token('elevation.surface'),
			'--p_lclPnlInset': '0px',
			'--p_lclPnlBorder': `${token('border.width')} solid ${token('color.border')}`,
			'--p_lclPnlShadow': 'none',
			gridTemplateAreas: `
                "main panel"
			`,
			gridTemplateRows: 'auto',
			// Share navigation-system's CSS variable name so both layouts honor the same minimum.
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values
			gridTemplateColumns: `minmax(var(${'--n_mainMinW' satisfies typeof mainMinimumWidthVar}, 20rem), 1fr) clamp(var(${localPanelMinWidthVar}), var(${localPanelResizingVar}, var(${localPanelTrackWidthVar})), calc(100% - var(${'--n_mainMinW' satisfies typeof mainMinimumWidthVar}, 20rem)))`,
		},
		overflow: 'auto',
	},
	mainContent: {
		gridArea: 'main',
		overflow: 'auto',
		display: 'flex',
		height: '100%',
		isolation: 'isolate',
	},
	panelUplift: {
		overflow: 'hidden',
	},
	managedInline: {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-selectors, @atlaskit/ui-styling-standard/no-nested-selectors
		'&&': {
			overflow: 'hidden',
			gridTemplateAreas: `'main panel'`,
			gridTemplateRows: 'auto',
			// Share navigation-system's CSS variable name so both layouts honor the same minimum.
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values
			gridTemplateColumns: `minmax(var(${'--n_mainMinW' satisfies typeof mainMinimumWidthVar}, 20rem), 1fr) clamp(var(${localPanelMinWidthVar}), var(${localPanelResizingVar}, var(${localPanelTrackWidthVar})), calc(100% - var(${'--n_mainMinW' satisfies typeof mainMinimumWidthVar}, 20rem)))`,
		},
	},
	managedOverlay: {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-selectors, @atlaskit/ui-styling-standard/no-nested-selectors
		'&&': {
			overflow: 'hidden',
			gridTemplateAreas: `'main'`,
			gridTemplateColumns: 'minmax(0, 1fr)',
			gridTemplateRows: '1fr',
		},
	},
	panelClosed: {
		// LayoutWithPanel can remain mounted without an active panel. Do not reserve its
		// panel track until PanelRouter has content to render into it.
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-selectors, @atlaskit/ui-styling-standard/no-nested-selectors
		'&&': {
			gridTemplateAreas: `'main'`,
			gridTemplateColumns: 'minmax(0, 1fr)',
			gridTemplateRows: '1fr',
		},
	},
});

export type LayoutWithPanelProps = {
	/**
	 * Panel width. Defaults to 365px, or 400px with `platform-dst-chat-panel-layout` enabled.
	 * Can be overridden via resizing.
	 */
	panelWidth?: number;
	onPanelWidthChange?: (panelWidth: number) => void;
	/**
	 * Minimum panel width. Defaults to 320px.
	 */
	panelMinWidth?: number;
	/**
	 * Optional application-defined maximum panel width.
	 */
	panelMaxWidth?: number;
	testId?: string;
	children: ReactNode;
	/**
	 * Called when resizing is complete.
	 * @param finalWidth number representing the new width of the panel in px
	 *
	 */
	onResizeEnd?: (finalWidth: number) => void;
};

export function LayoutWithPanel({
	children,
	panelWidth,
	onPanelWidthChange,
	panelMinWidth = 320,
	panelMaxWidth,
	testId,
	onResizeEnd,
}: LayoutWithPanelProps): JSX.Element {
	const manager = usePanelActionsInternal();
	const state = usePanelStateInternal();

	const [defaultPanelWidth] = useState(panelWidth ?? state.panelWidth);
	const [compactDefaultPanelWidth] = useState(panelWidth ?? 320);
	const isWidthPersistenceEnabled = fg('platform-dst-panel-system-width-persistence');
	const isControlled =
		isWidthPersistenceEnabled && panelWidth !== undefined && onPanelWidthChange !== undefined;
	const [persistedPanelWidth, setPersistedPanelWidth] = useControlled(
		isControlled ? panelWidth : undefined,
		() => defaultPanelWidth,
	);
	const panelSlotTestId = testId && `${testId}--panel-slot`;
	const layoutRef = useRef<HTMLDivElement | null>(null);
	const {
		prepareResize,
		state: managedSizing,
		getResizeBounds,
		startResize,
		resize,
		completeResize,
	} = UNSAFE_useLayoutAreaSizing({
		area: 'panel',
		config: {
			requestedWidth: isControlled ? panelWidth : undefined,
			defaultWidth: defaultPanelWidth,
			compactDefaultWidth: compactDefaultPanelWidth,
			minWidth: panelMinWidth,
			maxWidth: panelMaxWidth,
			isOpen: Boolean(state.activePanel),
		},
	});
	const panelTrackWidth =
		managedSizing?.width ??
		Math.max(
			panelMinWidth,
			Math.min(persistedPanelWidth, panelMaxWidth ?? Number.POSITIVE_INFINITY),
		);
	const onInlineResizeEnd = useCallback(
		(finalWidth: number) => {
			if (!isWidthPersistenceEnabled) {
				setPersistedPanelWidth(finalWidth);
			}
		},
		[isWidthPersistenceEnabled, setPersistedPanelWidth],
	);
	const handleWidthChange = useCallback(
		(width: number) => {
			if (isWidthPersistenceEnabled) {
				setPersistedPanelWidth(width);
				onPanelWidthChange?.(width);
			}
		},
		[isWidthPersistenceEnabled, onPanelWidthChange, setPersistedPanelWidth],
	);
	useEffect(() => {
		manager.setPanelWidth(persistedPanelWidth);
	}, [manager, persistedPanelWidth]);

	const activePanelRef = useRef(state.activePanel);
	const managerRef = useRef(manager);

	// Keep refs up to date on every render so the cleanup always sees the latest values
	activePanelRef.current = state.activePanel;
	managerRef.current = manager;

	useEffect(() => {
		/**
		 * Close the active panel when this component unmounts.
		 * Ensures panels do not persist when navigating to a new page.
		 */
		return () => {
			if (activePanelRef.current) {
				managerRef.current.closePanel(activePanelRef.current.instanceId);
			}
		};
	}, []);

	return (
		<div
			css={styles.container}
			// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop, @atlaskit/ui-styling-standard/no-unsafe-values
			style={{ [localPanelMinWidthVar]: `${panelMinWidth}px` } as CSSProperties}
		>
			{/* Resolve the actual host width before querying the remaining capacity. */}
			{/* A fixed literal only: React 18 otherwise HTML-escapes the CSS syntax during SSR. */}
			{/* eslint-disable-next-line @atlaskit/ui-styling-standard/no-global-styles, react/no-danger */}
			<style
				dangerouslySetInnerHTML={{
					__html: `@property --p_lclPnlHostW { syntax: '<length>'; inherits: true; initial-value: 0px; }`,
				}}
			/>
			<div css={styles.fitContainer}>
				<div
					css={[
						styles.layout,
						fg('platform-dst-motion-uplift-panel') && styles.panelUplift,
						managedSizing?.mode === 'inline' && styles.managedInline,
						managedSizing?.mode === 'overlay' && styles.managedOverlay,
						!state.activePanel && styles.panelClosed,
					]}
					data-testid={testId}
					ref={layoutRef}
					style={
						{
							// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop, @atlaskit/ui-styling-standard/no-unsafe-values
							[localPanelTrackWidthVar]: `${panelTrackWidth}px`,
							// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop, @atlaskit/ui-styling-standard/no-unsafe-values
							[localPanelMinWidthVar]: `${panelMinWidth}px`,
						} as CSSProperties
					}
				>
					<div css={styles.mainContent}>{children}</div>
					<PanelsRenderer>
						<PanelSlot
							defaultWidth={defaultPanelWidth}
							compactDefaultWidth={compactDefaultPanelWidth}
							initialInlineWidth={persistedPanelWidth}
							width={isControlled ? panelWidth : undefined}
							onWidthChange={handleWidthChange}
							layoutRef={layoutRef}
							managedSizing={managedSizing as UNSAFE_LayoutAreaState | undefined}
							getManagedResizeBounds={getResizeBounds}
							prepareResize={prepareResize}
							minWidth={panelMinWidth}
							maxWidth={panelMaxWidth}
							onManagedResizeStart={startResize}
							onManagedResize={resize}
							onManagedResizeEnd={completeResize}
							onInlineResizeEnd={onInlineResizeEnd}
							onResizeEnd={onResizeEnd}
							testId={panelSlotTestId}
						>
							<StablePanelRouter />
						</PanelSlot>
					</PanelsRenderer>
				</div>
			</div>
		</div>
	);
}
